import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 韩漫之家" description="抱歉，您查找的漫画不存在或已被移除。" />
        <Navbar />
        <main className="pt-16 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center py-20">
            <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
            <h1 className="mt-4 text-xl font-heading text-foreground-900">漫画未找到</h1>
            <p className="mt-2 text-sm text-foreground-500">抱歉，您查找的漫画不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.subcategory === comic.subcategory))
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: `${comic.title} - 韩漫之家`,
    description: comic.description,
    url: `http://uyeqdpvovrg.cn/comic/${comic.id}`,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.chapters[0]?.date || '2026-07-06',
    genre: [comic.category, comic.subcategory].filter(Boolean),
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫之家',
      url: 'http://uyeqdpvovrg.cn/',
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 免费韩漫在线阅读 - 韩漫之家`}
        description={`${comic.title}：${comic.description.slice(0, 120)}... 免费韩国漫画下拉式在线阅读，${comic.status}，评分${comic.rating}。`}
        keywords={`${comic.title},${comic.category}韩漫,免费韩漫,韩国漫画,韩漫之家`}
        canonicalUrl={`http://uyeqdpvovrg.cn/comic/${comic.id}`}
        ogTitle={`${comic.title} - 免费韩漫在线阅读 - 韩漫之家`}
        ogDescription={comic.description.slice(0, 160)}
        ogType="article"
        ogImage={comic.cover}
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: `${comic.category}漫画`, href: `/category/${comic.category === '热血' ? 'rexue' : comic.category === '恋爱' ? 'lianai' : comic.category === '奇幻' ? 'qihuan' : comic.category === '悬疑' ? 'xuanyi' : comic.category === '搞笑' ? 'gaoxiao' : 'xiaoyuan'}` },
            { label: comic.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-shrink-0 w-full lg:w-[300px]">
              <div className="rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 免费韩漫在线阅读`}
                  title={`${comic.title} - 免费韩漫在线阅读`}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">{comic.title}</h1>
              <p className="text-sm text-foreground-500 mt-1">{comic.altTitle}</p>

              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                  comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-foreground-200 text-foreground-700'
                }`}>
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 text-sm text-primary-500 font-medium">
                  <i className="ri-star-fill"></i> {comic.rating}
                </span>
                <span className="text-sm text-foreground-500">{comic.views} 阅读</span>
                <span className="text-sm text-foreground-500">{comic.updateDay}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 bg-background-100 rounded-lg p-5">
                <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500">
                <span><strong className="text-foreground-700">作者：</strong>{comic.author}</span>
                <span><strong className="text-foreground-700">画师：</strong>{comic.artist}</span>
              </div>
            </div>
          </div>

          <section className="mt-12">
            <h2 className="text-lg font-semibold text-foreground-900 mb-4">
              章节列表
              <span className="text-sm text-foreground-500 font-normal ml-2">
                共 {comic.chapters.length} 话
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {comic.chapters.map((ch) => (
                <div
                  key={ch.num}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {ch.isNew && (
                      <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500"></span>
                    )}
                    <span className="text-sm text-foreground-700 truncate">
                      第{ch.num}话 {ch.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{ch.date}</span>
                </div>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mt-16">
              <h2 className="text-lg font-semibold text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
                {relatedComics.map((c) => (
                  <ComicCard
                    key={c.id}
                    id={c.id}
                    title={c.title}
                    author={c.author}
                    cover={c.cover}
                    category={c.category}
                    status={c.status}
                    rating={c.rating}
                    views={c.views}
                    latestChapter={`第${c.chapters[0].num}话`}
                    isNew={c.chapters.some((ch) => ch.isNew)}
                  />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}